/* eslint-disable max-lines */
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MultiStepDialog } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { v4 as uuid } from 'uuid';

import {
  CreateTaskDialogPage_InterviewPlanFragment,
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationSettingsInput,
  InterviewMeetingLocationType,
  OptionsInput,
  SelfScheduleLocation,
  SelfScheduleOptionsInterviewPlanQuery,
  SelfScheduleOptionsModal_JobStageSettingsV2Fragment,
  SelfScheduleOptionsModal_SelfScheduleFragment,
  SelfScheduleOptionsUpsertInput,
  SelfScheduleOptions_InterviewMeetingLocationFragment,
  StringOrgPref,
  TaskCreationSource,
  useCandidateSelfScheduleEmailQuery,
  useSelfScheduleCalendarsQuery,
  useSelfScheduleOptionsInterviewPlanLazyQuery,
  useSelfScheduleOptionsInterviewPlanQuery,
  useSelfScheduleOptionsModalQuery,
} from 'src/generated/mloop-graphql';

import { CreateType } from 'src/components/Dialogs/ShareDialog';

import { resolveSelfScheduleVideoMeetingLinkScope } from 'src/entities/InterviewMeetingLocationModal/utils';
import cloneInterviewPlan from 'src/entities/InterviewPlan/cloneInterviewPlan';
import getSaveInterviewPlanInput from 'src/entities/InterviewPlan/getSaveInterviewPlanInput';

import useIntegrations from 'src/hooks/api/integration/useIntegrations';
import { useOrgPrefString } from 'src/hooks/api/org';
import useEmployeeId from 'src/hooks/useEmployeeId';

import { OrgPrefName } from 'src/utils/api/org';

import { SECONDS_IN_AN_HOUR, SECONDS_IN_A_DAY } from 'src/constants';

import { TaskShareModalContext } from '../ApplicationDetailsTaskView/ApplicationContent/TaskShareDialogHandler';
import CreateTaskDialogPage from '../ScheduleTask/CreateModals/shared/CreateTaskDialogPage';
import { TaskCreateInputWrapper, generateIdempotencyTaskId } from '../ScheduleTask/CreateModals/shared/types';
import useDefaultTaskAssignee from '../ScheduleTask/CreateModals/shared/useDefaultTaskAssignee';

import SelfScheduleEmail from './SelfScheduleEmail';
import SelfScheduleOptions, { getInclusionDays } from './SelfScheduleOptions';
import { SelfScheduleSelectedTemplates } from './SelfSchedulePreferences';
import { NumberOfDays } from './TimeframePicker';
import { useSelfScheduleJobStageTemplates } from './useSelfScheduleJobStageTemplates';
import useSelfScheduleOptionsUpsert from './useSelfScheduleOptionsUpsert';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQL_CANDIDATE_INFO = gql`
  query CandidateSelfScheduleEmail($candidateId: uuid!) {
    candidate(id: $candidateId) {
      email
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQL_SELF_SCHEDULE = gql`
  ${CreateTaskDialogPage.fragments.application}
  query SelfScheduleOptionsModal($applicationId: uuid!, $zoomUserEmployeeIds: [uuid!]!) {
    application(id: $applicationId) {
      id
      ...CreateTaskDialogPage_application
    }
    zoomUsersByIds(input: { employeeIds: $zoomUserEmployeeIds }) {
      zoomUserId
      employeeId
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SelfScheduleCalendars = gql`
  query SelfScheduleCalendars($input: InitScheduleFlowInput!) {
    initScheduleFlow(input: $input) {
      candidateCalendar {
        remoteId
      }
      interviewerCalendar {
        remoteId
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const JobStageInterviewPlan = gql`
  ${CreateTaskDialogPage.fragments.interviewPlan}
  ${cloneInterviewPlan.fragments.interviewPlan}
  ${getSaveInterviewPlanInput.fragments.interviewPlan}
  ${SelfScheduleOptions.fragments.interviewPlan}

  query SelfScheduleOptionsInterviewPlan($jobStageId: uuid!) {
    jobStage(id: $jobStageId) {
      id
      ...CreateTaskDialogPage_interviewPlan
      ...cloneInterviewPlan_interviewPlan
      ...getSaveInterviewPlanInput_interviewPlan
      ...SelfScheduleOptions_interviewPlan
    }
  }
`;

export enum SelfScheduleOptionsModalPage {
  OPTIONS = 0,
  SHARE_SELF_SCHEDULE = 1,
  CREATE_TASK = 2,
}

// using this directly in useState is causing a linting error, need to investigate or find a better way to handle this
const getOptions = ({
  jobStageSettingsV2,
}: {
  jobStageSettingsV2?: SelfScheduleOptionsModal_JobStageSettingsV2Fragment | null;
}): OptionsInput => {
  const rollingDays = () => {
    if (jobStageSettingsV2?.selfScheduleSettingsV2?.useRollingDays.value) {
      return jobStageSettingsV2?.selfScheduleSettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY;
    }
    return undefined;
  };
  return {
    candidateCalendarId: jobStageSettingsV2?.candidateCalendarId?.remoteCalendarId || '',
    interviewerCalendarId: jobStageSettingsV2?.interviewerCalendarId?.remoteCalendarId || '',
    location: SelfScheduleLocation.None,
    isPrivateCalendarEvent: jobStageSettingsV2?.isPrivateCalendarEvent.value || false,
    rollingDays: rollingDays(),
    advanceNoticeInHours:
      jobStageSettingsV2?.selfScheduleSettingsV2?.advanceNotice.duration.seconds / SECONDS_IN_AN_HOUR,
    shouldRespectLoadLimit: jobStageSettingsV2?.selfScheduleSettingsV2?.shouldRespectLoadLimit.value,
    canScheduleOverAvailableKeywords:
      jobStageSettingsV2?.selfScheduleSettingsV2?.canScheduleOverAvailableKeywords.value,
    canScheduleOverRecruitingKeywords:
      jobStageSettingsV2?.selfScheduleSettingsV2?.canScheduleOverRecruitingKeywords.value,
    canScheduleOverFreeTime: jobStageSettingsV2?.selfScheduleSettingsV2?.canScheduleOverFreeTime.value,
    note: jobStageSettingsV2?.selfScheduleSettingsV2?.candidateNote.value,
    candidateEventAdditionalAttendees:
      jobStageSettingsV2?.candidateEventAdditionalAttendees?.notificationPreference || undefined,
    interviewerEventAdditionalAttendees:
      jobStageSettingsV2?.interviewerEventAdditionalAttendees?.notificationPreference || undefined,
  };
};

export type Fragments = {
  selfSchedule: SelfScheduleOptionsModal_SelfScheduleFragment | undefined;
  jobStageSettingsV2: SelfScheduleOptionsModal_JobStageSettingsV2Fragment | undefined;
  interviewMeetingLocation: SelfScheduleOptions_InterviewMeetingLocationFragment | undefined;
};

export type Props = {
  applicationId: string;
  candidateId: string;
  jobStageId: string;
  jobId: string;
  /**
   * If taskId is provided, the modal will skip the task create step
   * If taskId is not provided, the modal will show the task create step and allow task creation
   */
  taskId?: string;
  showTaskCreateStep?: boolean;
  customJobStageId?: string;
  selfScheduleOptionsModalPage?: SelfScheduleOptionsModalPage;
  /** Allow consumers to turn off the send later option if the modal is shown from the share modal */
  showSendLater?: boolean;
  onClose: (emailSent?: boolean) => void;
  onJobStageIdChange?: (jobStageId: string) => void;
};

export const SelfScheduleOptionsModal: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidateId,
  jobStageId,
  taskId,
  jobId,
  showTaskCreateStep,
  customJobStageId,
  selfSchedule,
  jobStageSettingsV2,
  selfScheduleOptionsModalPage,
  onClose,
  showSendLater = true /** Normally default to false for boolean props, but this is a new prop chaning default behavior so defaulting the other way */,
  onJobStageIdChange,
  interviewMeetingLocation,
}) => {
  const { t } = useTranslation();
  const shareContext = useContext(TaskShareModalContext);
  const orgVideoMeetingLinkScopeEnabled = useFlag('org_video_meeting_link_scope');
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const collaborationLinkEnabled = useFlag('prd-155-codesignal-template-select');

  const [currentPage, setCurrentPage] = useState(selfScheduleOptionsModalPage || SelfScheduleOptionsModalPage.OPTIONS);
  const employeeId = useEmployeeId();

  const [selfScheduleOptions, setSelfScheduleOptions] = useState<SelfScheduleOptionsUpsertInput>({
    taskId,
    candidateEmailContent: {
      toEmailAddress: '',
      description: '',
      summary: '',
    },
    options: getOptions({ jobStageSettingsV2 }),
    interviewMeetingLocationSetting: {
      interviewMeetingLocationType:
        interviewMeetingLocation?.interviewMeetingLocationType || InterviewMeetingLocationType.None,
      customLocation: interviewMeetingLocation?.customLocation || undefined,
      dynamicHost:
        interviewMeetingLocation?.dynamicHost === DynamicInterviewMeetingHost.Interviewers
          ? interviewMeetingLocation?.dynamicHost
          : undefined,
      hostEmployeeId: interviewMeetingLocation?.hostEmployeeId || undefined,
      remoteVideoMeetingHostUserId: interviewMeetingLocation?.remoteVideoMeetingHostUserId || undefined,
      videoMeetingLinkScope: resolveSelfScheduleVideoMeetingLinkScope({
        enabled: orgVideoMeetingLinkScopeEnabled,
        interviewMeetingLocationType: interviewMeetingLocation?.interviewMeetingLocationType,
        dynamicHost: interviewMeetingLocation?.dynamicHost,
        existingScope: interviewMeetingLocation?.videoMeetingLinkScope,
      }),
    },
  });

  const { assignee, loading: assigneeLoading } = useDefaultTaskAssignee({
    applicationId,
    prefName: OrgPrefName.STR_TASK_DEFAULT_ASSIGNEE_SELF_SCHEDULE_REQUESTS,
  });

  const { data: modalData, loading: modalDataLoading } = useSelfScheduleOptionsModalQuery({
    variables: {
      applicationId,
      zoomUserEmployeeIds:
        interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.Zoom &&
        interviewMeetingLocation.dynamicHost === DynamicInterviewMeetingHost.Scheduler
          ? [employeeId]
          : [],
    },
    skip: !applicationId,
  });

  // Generate stable task ID for idempotency - persists across retries
  const idempotencyTaskId = useMemo(() => generateIdempotencyTaskId(), []);

  const [input, setInput] = useState<TaskCreateInputWrapper>({
    applicationId,
    jobStageId,
    assigneeEmployeeId: assignee?.id,
    assignee,
    candidateTimezone: getLocalTimezone(),
    isUrgent: false,
    isDebriefRequired: false,
    creationSource: TaskCreationSource.CandidateAvailabilityRequest,
    customInterviewPlan: [],
    customJobStageId: null,
    taskCreatorId: employeeId,
    taskId: idempotencyTaskId,
  });

  const [interviewPlan, setInterviewPlan] = useState<CreateTaskDialogPage_InterviewPlanFragment>({
    id: '',
    jobStageInterviewGroups: [],
  });

  const { data: integrations } = useIntegrations();
  const { templates: selfScheduleTemplates } = useSelfScheduleJobStageTemplates(jobStageId);

  const { data: candidateData } = useCandidateSelfScheduleEmailQuery({ variables: { candidateId } });
  const { data: calendars } = useSelfScheduleCalendarsQuery({
    variables: {
      input: {
        jobStageId,
      },
    },
  });

  const [defaultCommentPrefValue, { loading: defaultTaskCommentLoading }] = useOrgPrefString(
    StringOrgPref.StrDefaultTaskComment
  );

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    if (modalDataLoading || !modalData?.application) {
      return;
    }

    if (modalData?.application?.candidate?.timezone) {
      setInput((prev) => ({
        ...prev,
        candidateTimezone: modalData.application?.candidate?.timezone,
      }));
    }

    if (modalData?.application?.job?.jobSettings.defaultTaskQueue?.id) {
      setInput((prev) => ({ ...prev, taskQueueId: modalData.application?.job?.jobSettings?.defaultTaskQueue?.id }));
    }
  }, [modalData, setInput, modalDataLoading]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    if (defaultTaskCommentLoading) {
      return;
    }

    setInput((prev) => ({
      ...prev,
      note: defaultCommentPrefValue || '',
    }));
  }, [defaultCommentPrefValue, defaultTaskCommentLoading, setInput]);

  // After we load the assignee, we need to set the assignee in the input
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (assigneeLoading) {
      return;
    }

    setInput((prev) => ({
      ...prev,
      assigneeEmployeeId: assignee?.id,
      assignee,
    }));
  }, [assigneeLoading, assignee, setInput]);

  const onInterviewPlanFetchCompleted = (data: SelfScheduleOptionsInterviewPlanQuery) => {
    if (!data.jobStage) {
      return;
    }

    const newCustomJobStageId = customJobStageId || uuid();

    const interviewPlanClone = cloneInterviewPlan(
      { interviewPlan: data.jobStage },
      { customJobStageId: newCustomJobStageId }
    );

    setInterviewPlan(interviewPlanClone);

    const interviewPlanInput = getSaveInterviewPlanInput(
      { interviewPlan: interviewPlanClone },
      { finalJobStageId: newCustomJobStageId, collaborationLinkEnabled }
    );

    setInput((prev) => ({
      ...prev,
      customJobStageId: newCustomJobStageId,
      customInterviewPlan: !orgBreakImprovementsEnabled ? interviewPlanInput.groups : undefined,
      interviewPlan: orgBreakImprovementsEnabled
        ? {
            groups: interviewPlanInput.groups,
            schedulingWindow: interviewPlanInput.schedulingWindow
              ? { seconds: interviewPlanInput.schedulingWindow.seconds }
              : undefined,
            excludedEmployeeIds: interviewPlanInput.excludedEmployeeIds || [],
          }
        : undefined,
    }));
  };

  const interviewPlanId = customJobStageId || input.jobStageId || jobStageId;
  const { loading: interviewPlanLoading } = useSelfScheduleOptionsInterviewPlanQuery({
    variables: { jobStageId: interviewPlanId },
    onCompleted: onInterviewPlanFetchCompleted,
  });

  const [fetchInterviewPlan, { loading: interviewPlanLazyLoading }] = useSelfScheduleOptionsInterviewPlanLazyQuery({
    variables: { jobStageId: interviewPlanId },
    onCompleted: onInterviewPlanFetchCompleted,
  });

  const selfScheduleOptionsUpsert = useSelfScheduleOptionsUpsert(selfScheduleOptions, taskId, input);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    // Don't use settings if selfSchedule is provided
    if (selfSchedule) {
      return;
    }

    setSelfScheduleOptions((prevOptions) => {
      const newSsOptions = {
        candidateEventTemplateId: jobStageSettingsV2?.candidateEventTemplateId?.templateId,
        candidateEmailTemplateId: jobStageSettingsV2?.candidateEmailTemplateId?.templateId,
        interviewerEventTemplateId: jobStageSettingsV2?.interviewerEventTemplateId?.templateId,
      };

      const rollingDays = () => {
        if (jobStageSettingsV2?.selfScheduleSettingsV2?.useRollingDays.value) {
          return jobStageSettingsV2?.selfScheduleSettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY;
        }
        return undefined;
      };

      let interviewMeetingLocationSetting: InterviewMeetingLocationSettingsInput;
      if (
        modalData?.zoomUsersByIds?.length &&
        modalData?.zoomUsersByIds?.length > 0 &&
        interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.Zoom &&
        interviewMeetingLocation.dynamicHost === DynamicInterviewMeetingHost.Scheduler
      ) {
        // When creating a self-schedule request we want to store the actual zoom info of the scheduler.
        interviewMeetingLocationSetting = {
          interviewMeetingLocationType: InterviewMeetingLocationType.Zoom,
          dynamicHost: DynamicInterviewMeetingHost.CustomZoomHost,
          hostEmployeeId: modalData?.zoomUsersByIds[0].employeeId,
          remoteVideoMeetingHostUserId: modalData?.zoomUsersByIds[0].zoomUserId,
          videoMeetingLinkScope: resolveSelfScheduleVideoMeetingLinkScope({
            enabled: orgVideoMeetingLinkScopeEnabled,
            interviewMeetingLocationType: InterviewMeetingLocationType.Zoom,
            dynamicHost: DynamicInterviewMeetingHost.CustomZoomHost,
            existingScope: interviewMeetingLocation?.videoMeetingLinkScope,
          }),
        };
      } else {
        interviewMeetingLocationSetting = {
          interviewMeetingLocationType:
            interviewMeetingLocation?.interviewMeetingLocationType || InterviewMeetingLocationType.None,
          dynamicHost:
            interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.GoogleMeet &&
            interviewMeetingLocation.dynamicHost === DynamicInterviewMeetingHost.Scheduler
              ? undefined
              : interviewMeetingLocation?.dynamicHost || undefined,
          hostEmployeeId:
            interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.GoogleMeet &&
            interviewMeetingLocation.dynamicHost === DynamicInterviewMeetingHost.Scheduler
              ? employeeId
              : interviewMeetingLocation?.hostEmployeeId || undefined,
          remoteVideoMeetingHostUserId: interviewMeetingLocation?.remoteVideoMeetingHostUserId || undefined,
          customLocation: interviewMeetingLocation?.customLocation || undefined,
          videoMeetingLinkScope: resolveSelfScheduleVideoMeetingLinkScope({
            enabled: orgVideoMeetingLinkScopeEnabled,
            interviewMeetingLocationType: interviewMeetingLocation?.interviewMeetingLocationType,
            dynamicHost: interviewMeetingLocation?.dynamicHost,
            existingScope: interviewMeetingLocation?.videoMeetingLinkScope,
          }),
        };
      }

      let ssOptions = {
        ...prevOptions,
        interviewMeetingLocationSetting,
        options: {
          ...prevOptions.options,
          candidateCalendarId: jobStageSettingsV2?.candidateCalendarId?.remoteCalendarId,
          interviewerCalendarId: jobStageSettingsV2?.interviewerCalendarId?.remoteCalendarId,
          location: SelfScheduleLocation.None,
          isPrivateCalendarEvent: jobStageSettingsV2?.isPrivateCalendarEvent.value || false,
          rollingDays: rollingDays(),
          advanceNoticeInHours:
            jobStageSettingsV2?.selfScheduleSettingsV2?.advanceNotice.duration.seconds / SECONDS_IN_AN_HOUR,
          shouldRespectLoadLimit: jobStageSettingsV2?.selfScheduleSettingsV2?.shouldRespectLoadLimit.value,
          canScheduleOverAvailableKeywords:
            jobStageSettingsV2?.selfScheduleSettingsV2?.canScheduleOverAvailableKeywords.value,
          inclusionDays: !rollingDays()
            ? getInclusionDays(
                jobStageSettingsV2?.selfScheduleSettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY
              )
            : undefined,
          canScheduleOverRecruitingKeywords:
            jobStageSettingsV2?.selfScheduleSettingsV2?.canScheduleOverRecruitingKeywords.value,
          canScheduleOverFreeTime: jobStageSettingsV2?.selfScheduleSettingsV2?.canScheduleOverFreeTime.value,
          note: jobStageSettingsV2?.selfScheduleSettingsV2?.candidateNote.value,
          candidateEventAdditionalAttendees:
            jobStageSettingsV2?.candidateEventAdditionalAttendees?.notificationPreference || undefined,
          interviewerEventAdditionalAttendees:
            jobStageSettingsV2?.interviewerEventAdditionalAttendees?.notificationPreference || undefined,
        },
        ...newSsOptions,
      } as SelfScheduleOptionsUpsertInput;

      if (!ssOptions.options.location) {
        if (integrations?.zoom?.active || integrations?.zoom_custom?.active) {
          ssOptions = {
            ...ssOptions,
            options: {
              ...ssOptions.options,
            },
          };
        } else if (integrations?.google) {
          ssOptions = {
            ...ssOptions,
            options: {
              ...ssOptions.options,
              location: SelfScheduleLocation.Google,
            },
          };
        } else if (
          integrations?.microsoft?.active ||
          integrations?.microsoft_custom?.active ||
          integrations?.microsoft_gcc_high?.active ||
          integrations?.microsoft_service_user?.service_account_email
        ) {
          ssOptions = {
            ...ssOptions,
            options: {
              ...ssOptions.options,
              location: SelfScheduleLocation.MicrosoftTeams,
            },
          };
        }
      }

      if (selfScheduleTemplates) {
        ssOptions = {
          ...ssOptions,
          ...selfScheduleTemplates,
        };

        // This is a special case since we use a checkbox to determine whether to send a personal email or not.
        // Always fallback to what's in settings. Don't use default template.
        const shouldSendEmailToCandidate = jobStageSettingsV2?.shouldSendEmailToCandidate?.value ?? false;
        ssOptions.candidateEmailTemplateId = shouldSendEmailToCandidate
          ? jobStageSettingsV2?.candidateEmailTemplateId?.templateId
          : undefined;
        ssOptions.interviewerEventTemplateId = jobStageSettingsV2?.interviewerEventTemplateId?.templateId;
      }

      return ssOptions;
    });
  }, [
    selfScheduleTemplates,
    integrations,
    selfSchedule,
    calendars,
    jobStageSettingsV2,
    employeeId,
    interviewMeetingLocation?.interviewMeetingLocationType,
    interviewMeetingLocation?.dynamicHost,
    interviewMeetingLocation?.hostEmployeeId,
    interviewMeetingLocation?.remoteVideoMeetingHostUserId,
    interviewMeetingLocation?.customLocation,
    interviewMeetingLocation?.videoMeetingLinkScope,
    orgVideoMeetingLinkScopeEnabled,
    modalData?.zoomUsersByIds,
  ]);

  // This effect is needed to update the options to match the loaded options from the backend
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes browser or analytics APIs after render.
  useEffect(() => {
    if (!selfSchedule) {
      return;
    }
    // this is the self schedule request.
    // aka this is the edit flow
    const ssOptions = selfSchedule;

    if (!ssOptions || !ssOptions.options) {
      return;
    }

    setSelfScheduleOptions({
      taskId,
      candidateEmailContent: {
        toEmailAddress: '',
        description: '',
        summary: '',
      },
      candidateEmailTemplateId: ssOptions.candidateEmailTemplateId,
      candidateEventTemplateId: selfScheduleTemplates?.candidateEventTemplateId,
      interviewerEventTemplateId: ssOptions.interviewerEventTemplateId,
      options: {
        candidateCalendarId: ssOptions.options.candidateCalendarId || '',
        interviewerCalendarId: ssOptions.options.interviewerCalendarId || '',
        location: SelfScheduleLocation.None,
        inclusionDays: ssOptions.options.inclusionDays,
        advanceNoticeInHours: ssOptions.options.advanceNoticeInHours,
        rollingDays: ssOptions.options.rollingDays,
        canScheduleOverAvailableKeywords: ssOptions.options.canScheduleOverAvailableKeywords,
        canScheduleOverRecruitingKeywords: ssOptions.options.canScheduleOverRecruitingKeywords,
        canScheduleOverFreeTime: ssOptions.options.canScheduleOverFreeTime,
        shouldRespectLoadLimit: ssOptions.options.shouldRespectLoadLimit,
        note: ssOptions.options.note,
        candidateEventAdditionalAttendees: ssOptions.options.candidateEventAdditionalAttendees,
        interviewerEventAdditionalAttendees: ssOptions.options.interviewerEventAdditionalAttendees,
      },
    });
  }, [selfSchedule, applicationId, jobStageId, taskId, selfScheduleTemplates]);

  const onOptionsBack =
    !taskId && showTaskCreateStep
      ? () => {
          setCurrentPage(SelfScheduleOptionsModalPage.CREATE_TASK);
        }
      : undefined;

  const taskCustomJobStageId = taskId || !showTaskCreateStep ? customJobStageId : input.customJobStageId;

  return (
    <MultiStepDialog open onClose={onClose}>
      {currentPage === SelfScheduleOptionsModalPage.CREATE_TASK &&
        showTaskCreateStep &&
        modalData?.application &&
        !assigneeLoading &&
        !taskId && (
          <CreateTaskDialogPage
            title={t('hiring-dashboard-schedule:schedule.selfScheduleModal.title')}
            input={input}
            setInput={setInput}
            interviewPlan={interviewPlan}
            interviewPlanLoading={interviewPlanLoading || interviewPlanLazyLoading}
            application={modalData.application}
            onClose={onClose}
            source={TaskCreationSource.SelfScheduleRequest}
            onContinue={(newInput) => {
              setInput(newInput);
              setCurrentPage(SelfScheduleOptionsModalPage.OPTIONS);
            }}
            onJobStageIdChange={(id) => {
              onJobStageIdChange?.(id);

              fetchInterviewPlan({ variables: { jobStageId: id } });
            }}
            onInterviewPlanChange={(newInterviewPlan) => {
              setInterviewPlan(newInterviewPlan);

              const interviewPlanInput = getSaveInterviewPlanInput(
                { interviewPlan: newInterviewPlan },
                { finalJobStageId: newInterviewPlan.id, collaborationLinkEnabled }
              );

              setInput((prev) => ({
                ...prev,
                customInterviewPlan: !orgBreakImprovementsEnabled ? interviewPlanInput.groups : undefined,
                interviewPlan: orgBreakImprovementsEnabled
                  ? {
                      groups: interviewPlanInput.groups,
                      schedulingWindow: interviewPlanInput.schedulingWindow
                        ? { seconds: interviewPlanInput.schedulingWindow.seconds }
                        : undefined,
                      excludedEmployeeIds: interviewPlanInput.excludedEmployeeIds || [],
                    }
                  : undefined,
              }));
            }}
          />
        )}
      {currentPage === SelfScheduleOptionsModalPage.OPTIONS && (
        <SelfScheduleOptions
          applicationId={applicationId}
          taskId={taskId}
          options={
            selfScheduleOptions?.options || {
              rollingDays: NumberOfDays.TwoWeeks,
            }
          }
          numberOfDays={jobStageSettingsV2?.selfScheduleSettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY}
          jobStageId={jobStageId}
          customJobStageId={taskCustomJobStageId}
          customInterviewPlan={
            !taskId ? input.customInterviewPlan || input.interviewPlan?.groups || undefined : undefined
          }
          interviewPlan={interviewPlan}
          interviewMeetingLocation={
            // The edited state is stored as an input type; the child reads its fields (incl. the
            // optional videoMeetingLinkScope) leniently, so present it as the read fragment shape.
            (selfScheduleOptions.interviewMeetingLocationSetting as SelfScheduleOptions_InterviewMeetingLocationFragment) ??
            interviewMeetingLocation
          }
          onBack={onOptionsBack}
          onClose={onClose}
          onContinue={() => setCurrentPage(SelfScheduleOptionsModalPage.SHARE_SELF_SCHEDULE)}
          onOptionsChanged={(options: OptionsInput) => {
            setSelfScheduleOptions({ ...selfScheduleOptions, options });
          }}
          templates={{
            interviewerEventTemplateId: selfScheduleOptions.interviewerEventTemplateId,
            candidateEmailTemplateId: selfScheduleOptions.candidateEmailTemplateId,
            candidateEventTemplateId: selfScheduleOptions.candidateEventTemplateId,
          }}
          defaultTemplates={{
            ...selfScheduleTemplates,
          }}
          onTemplatesChanged={(selectedTemplates: SelfScheduleSelectedTemplates) => {
            setSelfScheduleOptions({
              ...selfScheduleOptions,
              interviewerEventTemplateId: selectedTemplates.interviewerEventTemplateId,
              candidateEmailTemplateId: selectedTemplates.candidateEmailTemplateId,
              candidateEventTemplateId: selectedTemplates.candidateEventTemplateId,
            });
          }}
          onInterviewMeetingLocationChanged={(value: InterviewMeetingLocationSettingsInput) => {
            const newValue = { ...value };
            if (
              value.interviewMeetingLocationType === InterviewMeetingLocationType.GoogleMeet &&
              value.dynamicHost === DynamicInterviewMeetingHost.Scheduler
            ) {
              newValue.dynamicHost = undefined;
              newValue.hostEmployeeId = employeeId;
            }
            setSelfScheduleOptions({
              ...selfScheduleOptions,
              interviewMeetingLocationSetting: newValue,
            });
          }}
        />
      )}
      {currentPage === SelfScheduleOptionsModalPage.SHARE_SELF_SCHEDULE && jobStageSettingsV2 && (
        <SelfScheduleEmail
          jobStageSettingsV2={jobStageSettingsV2}
          taskId={taskId}
          assigneeId={input.assigneeEmployeeId}
          jobId={jobId}
          canSaveWithoutSending={!!candidateData?.candidate?.email}
          applicationId={applicationId}
          candidateId={candidateId}
          jobStageId={jobStageId}
          taskCreatorId={employeeId}
          subscriberEmployeeIds={input?.subscriberEmployeeIds || undefined}
          subTitle={
            taskId || !showTaskCreateStep
              ? t('hiring-dashboard-schedule:schedule.selfScheduleEmail.stepWithTask')
              : t('hiring-dashboard-schedule:schedule.selfScheduleEmail.stepWithoutTask')
          }
          selfScheduleOptionsUpsert={selfScheduleOptionsUpsert}
          goBack={
            selfScheduleOptionsModalPage !== undefined && showSendLater
              ? () => setCurrentPage(SelfScheduleOptionsModalPage.OPTIONS)
              : undefined
          }
          onCancel={onClose}
          onSave={(emailSent: boolean, createdTaskId: string | null) => {
            if (shareContext?.onTaskShare && emailSent !== undefined && !emailSent) {
              shareContext.onTaskShare(CreateType.SELF_SCHEDULE, createdTaskId ?? '');
            }

            onClose(emailSent);
          }}
          ccRecipients={jobStageSettingsV2?.selfScheduleSettingsV2?.ccRecipients.notificationPreference}
          bccRecipients={jobStageSettingsV2?.selfScheduleSettingsV2?.bccRecipients.notificationPreference}
        />
      )}
    </MultiStepDialog>
  );
};

SelfScheduleOptionsModal.fragments = {
  selfSchedule: gql`
    ${SelfScheduleOptions.fragments?.options}
    ${SelfScheduleOptions.fragments.interviewMeetingLocation}
    fragment SelfScheduleOptionsModal_selfSchedule on SelfScheduleOptions {
      id
      taskId
      createdAt
      creatorEmployeeId
      deletedAt
      jobStageId
      candidateEmailTemplateId
      candidateEventTemplateId
      interviewerEventTemplateId
      lastUpdatedByEmployeeId
      options {
        ...SelfScheduleOptions_options
      }
      rescheduledAt
      selfScheduleEmailTemplateId
      selfScheduleEmailSentAt
      updatedAt
      interviewMeetingLocationSetting {
        ...SelfScheduleOptions_interviewMeetingLocation
      }
    }
  `,
  jobStageSettingsV2: gql`
    ${SelfScheduleOptions.fragments.interviewMeetingLocation}
    ${SelfScheduleEmail.fragments.jobStageSettingsV2}
    fragment SelfScheduleOptionsModal_jobStageSettingsV2 on JobStageSettingsV2 {
      id
      ...SelfScheduleEmail_jobStageSettingsV2
      candidateCalendarId {
        remoteCalendarId
      }
      interviewerCalendarId {
        remoteCalendarId
      }
      isPrivateCalendarEvent {
        value
      }
      candidateEventTemplateId {
        templateId
      }
      shouldSendEmailToCandidate {
        value
      }
      candidateEmailTemplateId {
        templateId
      }
      interviewerCalendarId {
        remoteCalendarId
      }
      interviewerEventTemplateId {
        templateId
      }
      interviewMeetingLocationSettingWithLevel {
        interviewMeetingLocationSetting {
          ...SelfScheduleOptions_interviewMeetingLocation
        }
      }
      isPrivateCalendarEvent {
        value
      }
      selfScheduleSettingsV2 {
        timeframe {
          duration {
            seconds
          }
        }
        advanceNotice {
          duration {
            seconds
          }
        }
        shouldRespectLoadLimit {
          value
        }
        canScheduleOverRecruitingKeywords {
          value
        }
        canScheduleOverAvailableKeywords {
          value
        }
        canScheduleOverFreeTime {
          value
        }
        candidateNote {
          value
        }
        ccRecipients {
          notificationPreference {
            dynamicAudiences
            employeeIds
            externalEmailAddresses
          }
        }
        bccRecipients {
          notificationPreference {
            dynamicAudiences
            employeeIds
            externalEmailAddresses
          }
        }
        useRollingDays {
          value
        }
      }
      candidateEventAdditionalAttendees {
        notificationPreference {
          dynamicAudiences
          employeeIds
          externalEmailAddresses
        }
      }
      interviewerEventAdditionalAttendees {
        notificationPreference {
          dynamicAudiences
          employeeIds
          externalEmailAddresses
        }
      }
    }
  `,
  interviewMeetingLocation: gql`
    ${SelfScheduleOptions.fragments.interviewMeetingLocation}
    fragment SelfScheduleOptionsModal_interviewMeetingLocation on InterviewMeetingLocationSettingPref {
      ...SelfScheduleOptions_interviewMeetingLocation
    }
  `,
};
